import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, ViewChildren, QueryList, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../../../../environments/environment.prod';
import { ToastrService } from 'ngx-toastr';
import * as feather from 'feather-icons';
import { NouveauPaiementComponent } from './nouveau-paiement/nouveau-paiement.component';
import { DepuisAvoirComponent } from './depuis-avoir/depuis-avoir.component';
import { PaiementNonAffComponent } from './paiement-non-aff/paiement-non-aff.component';
import { PaiementService } from '../../../../../services/paiement/paiement.service';
import {Paiement} from '../../../../../models/paiement/Paiement';

@Component({
    selector: 'app-paiement-facture',
    templateUrl: './paiement-facture.component.html',
    styleUrls: ['./paiement-facture.component.scss']
})
export class PaiementFactureComponent implements OnInit, AfterViewInit {
    @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
    @ViewChildren('nouveauPaiement') nouveauPaiementComponents!: QueryList<NouveauPaiementComponent>;
    @ViewChildren('paiementNonAff') paiementNonAffComponents!: QueryList<PaiementNonAffComponent>;
    @ViewChildren(DepuisAvoirComponent) depuisAvoirComponents!: QueryList<DepuisAvoirComponent>;
    @Output() paiementSuccess = new EventEmitter<{ paiement: Paiement, documentId: number, avoirDocumentId?: number, avoirStatutId?: number }>();

    /** Incrémenté après création d'un paiement pour recharger la liste avoirs (MONTANT RESTANT). */
    refreshAvoirsTrigger = 0;

    file: string = environment.file_url;
    selectedFiles: File[] = [];
    selectedFile: File | null = null;
    private modalRef!: NgbModalRef;
    activeTab: 'nouveau-paiement' | 'paiement-non-affectes' | 'depuis-un-avoir' = 'nouveau-paiement';
    selectedDocumentType: string | null = null; // Type de document : 'facture' (vente) ou 'facture-fournisseur' (achat)

    selectedFacture: any = null;
    selectedAvoir: any = null; // Avoir sélectionné depuis l'onglet "Depuis un avoir" pour pré-remplir le nouveau paiement
    factureMontant: number = 0; // Total TTC avec timbre
    factureTimbreFiscal: number = 0; // Montant du timbre fiscal
    resteAPayer: number = 0;
    /** Reste à payer TTC calculé par nouveau-paiement (net à payer − total saisi) ; utilisé pour l'affichage quand l'onglet est Nouveau paiement. */
    resteAPayerFromChild: number | null = null;
    triggerCreatePaiement: boolean = false; // Déclencheur pour créer le paiement

    constructor(
        private toastr: ToastrService,
        private modalService: NgbModal,
        private paiementService: PaiementService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.activeTab = 'nouveau-paiement';
    }

    ngAfterViewInit(): void {
        feather.replace();
        // Forcer la détection des changements pour que @ViewChild trouve les composants
        this.cdr.detectChanges();
    }

    openModal(facture?: any, explicitType?: string): void {
        this.selectedFacture = facture;
        if (facture) {
            const fullData = facture?.fullData || facture;

            // Déterminer le type de document (vente, achat ou avoir) avant d'extraire les montants
            // PRIORITÉ 1: Toujours vérifier si le document est un avoir (statut "Non utilisé" / "Partiellement utilisé" ou type Avoir)
            //    car en liste Facture on peut avoir des avoirs (explicitType = 'facture' mais document = avoir)
            // Statut ID: 19 = Non utilisé, 20 = Partiellement utilisé (voir depuis-avoir.component)
            const statutId = fullData.statut_id ?? fullData.statut?.id ?? facture.statut_id ?? facture.statut?.id;
            const statutLabel = this.getDocumentStatut(facture);
            // facture.status = libellé du statut dans la liste (DataService: item.statut?.titre || status)
            const statusFromList = (facture.status ?? '').toString().trim();
            const typeDocument = (fullData.typeDocument?.titre || fullData.type_document?.titre || fullData.typeDocument?.type
                || fullData.type_document?.type || facture.typeDocument?.titre || facture.type_document?.titre
                || facture.typeDocument?.type || facture.type_document?.type || facture.type || '').toString().trim();
            const typeDocumentUpper = typeDocument.toUpperCase();
            const isStatutAvoir = this.isStatutAvoirById(statutId)
                || this.isStatutAvoirByLabel(statutLabel)
                || this.isStatutAvoirByLabel(statusFromList);
            const isTypeAvoir = typeDocumentUpper === 'AVOIR' || typeDocumentUpper.includes('AVOIR');

            const isAvoir = isStatutAvoir || isTypeAvoir;
            if (isAvoir) {
                this.selectedDocumentType = 'avoir';
                console.log('📋 [openModal] Type avoir détecté (statut:', statutLabel, ', type:', typeDocument, ')');
            } else if (explicitType === 'facture-fournisseur') {
                this.selectedDocumentType = 'facture-fournisseur';
                console.log('📋 [openModal] Type explicite (facture-fournisseur)');
            } else if (explicitType === 'facture') {
                this.selectedDocumentType = 'facture';
                console.log('📋 [openModal] Type explicite (facture)');
            } else {
                this.selectedDocumentType = this.isAchatDocument(typeDocumentUpper) ? 'facture-fournisseur' : 'facture';
                console.log('📋 [openModal] Type déduit:', this.selectedDocumentType);
            }

            // Montant total du document : pour un avoir, utiliser total_ttc (valeur positive) comme dans Depuis un avoir
            if (isAvoir) {
                const totalTtcRaw = fullData.total_ttc ?? facture.total_ttc ?? fullData.net_payer ?? facture.net_payer ?? facture.revenue ?? 0;
                const parsed = parseFloat(String(totalTtcRaw).replace(/[^\d.,\-]/g, '').replace(',', '.'));
                this.factureMontant = Math.abs(isNaN(parsed) ? 0 : parsed);
            } else {
                const netPayer = fullData.net_payer ?? facture.net_payer ?? facture.revenue ?? '0';
                this.factureMontant = parseFloat(String(netPayer).replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            }
            // Timbre fiscal (par défaut 0.6 pour les factures)
            this.factureTimbreFiscal = parseFloat(facture.timbre_fiscal || facture.timbreFiscal || '0.6') || 0.6;
            
            console.log('📋 [openModal] selectedDocumentType final:', this.selectedDocumentType);
            console.log('📋 [openModal] factureMontant:', this.factureMontant, '(isAvoir:', isAvoir, ')');
            console.log('📋 [openModal] facture complète:', facture);
            
            // Log détaillé pour déboguer l'extraction du client_id (fullData déjà déclaré plus haut)
            console.log('🔍 [openModal] Structure de la facture pour extraction client_id:', {
                facture_client_id: facture.client_id,
                fullData_client_id: fullData.client_id,
                facture_clients: facture.clients,
                fullData_clients: fullData.clients,
                facture_client_data: facture.client_data,
                fullData_client_data: fullData.client_data,
                facture_keys: Object.keys(facture),
                fullData_keys: fullData ? Object.keys(fullData) : null
            });
            
            const extractedClientId = this.getClientId(facture);
            console.log('🔍 [openModal] client_id extrait depuis la facture:', extractedClientId);

            // Initialiser le reste à payer avec le montant de la facture en attendant le calcul asynchrone
            this.resteAPayer = this.factureMontant;
            console.log('💰 [openModal] resteAPayer initialisé à:', this.resteAPayer, 'TND (factureMontant)');
            
            // Calculer le reste à payer de manière asynchrone
            this.calculateResteAPayer();
        }

        this.resteAPayerFromChild = null;

        const options: NgbModalOptions = {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            ariaLabelledBy: 'fileUploadModalLabel',
            windowClass: 'custom-modal'
        };
        this.modalRef = this.modalService.open(this.modalTemplate, options);

        // Le composant se chargera automatiquement via @Input() quand l'onglet est activé
        // Pas besoin de charger manuellement ici
    }

    onResteAPayerCalcule(value: number): void {
        // Déporter au cycle suivant pour éviter NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
        setTimeout(() => {
            this.resteAPayerFromChild = value;
            this.cdr.detectChanges();
        }, 0);
    }

    /** payload = { avoir, montantRestant } pour pré-remplir le nouveau paiement avec le reste. */
    onUtiliserAvoir(payload: { avoir: any; montantRestant: string } | any): void {
        this.selectedAvoir = payload?.avoir != null ? payload : payload;
        this.setActiveTab('nouveau-paiement');
        this.cdr.detectChanges();
    }

    /** Créer le paiement avec l'avoir : pré-remplit le formulaire puis déclenche la création après un court délai. */
    onAjouterPaiementAvoir(payload: { avoir: any; montantRestant: string }): void {
        this.selectedAvoir = payload?.avoir != null ? payload : payload;
        this.setActiveTab('nouveau-paiement');
        this.cdr.detectChanges();
        setTimeout(() => this.validatePaiement(), 450);
    }

    setActiveTab(tab: 'nouveau-paiement' | 'paiement-non-affectes' | 'depuis-un-avoir'): void {
        this.activeTab = tab;
        feather.replace();

        // Si on passe à l'onglet "paiements non affectés", le composant se chargera automatiquement via @Input()
        if (tab === 'paiement-non-affectes') {
            console.log('🔄 [setActiveTab] Passage à l\'onglet paiements non affectés');
            console.log('🔄 [setActiveTab] document_id:', this.selectedFacture?.id);
            // Le composant se chargera automatiquement via ngOnChanges quand documentId est passé via @Input()
            this.cdr.detectChanges();
        }
    }

    convertDocument(): void {
        if (this.activeTab === 'nouveau-paiement') {
            const nouveauPaiementComponent = this.nouveauPaiementComponents?.first;
            if (nouveauPaiementComponent) {
                nouveauPaiementComponent.clientId = this.getClientId(this.selectedFacture);
                nouveauPaiementComponent.fournisseurId = this.getFournisseurId(this.selectedFacture);
                nouveauPaiementComponent.createPaiement();
            } else {
                this.toastr.warning('Formulaire de paiement non disponible', 'Attention');
            }
        } else {
            this.toastr.success('Action effectuée avec succès !', 'Succès', {
                toastClass: 'toast ngx-toastr',
                closeButton: true
            });
            this.modalRef.close('Confirm');
        }
    }

    validatePaiement(): void {
        console.log('🔘 [validatePaiement] Bouton Valider cliqué');
        console.log('🔘 [validatePaiement] activeTab:', this.activeTab);
        console.log('🔘 [validatePaiement] selectedFacture:', this.selectedFacture);

        // Méthode spécifique pour le bouton "Valider"
        if (this.activeTab === 'nouveau-paiement') {
            // Forcer la détection des changements pour trouver le composant
            this.cdr.detectChanges();

            // Essayer d'abord avec ViewChildren
            const nouveauPaiementComponent = this.nouveauPaiementComponents?.first;

            if (nouveauPaiementComponent) {
                console.log('✅ [validatePaiement] Composant trouvé via ViewChildren, appel de createPaiement()');
                nouveauPaiementComponent.clientId = this.getClientId(this.selectedFacture);
                nouveauPaiementComponent.fournisseurId = this.getFournisseurId(this.selectedFacture);
                console.log('✅ [validatePaiement] clientId / fournisseurId injectés');
                nouveauPaiementComponent.createPaiement();
            } else {
                console.warn('⚠️ [validatePaiement] Composant non trouvé via ViewChildren, utilisation du trigger');
                console.warn('⚠️ [validatePaiement] Nombre de composants trouvés:', this.nouveauPaiementComponents?.length || 0);

                // Utiliser le trigger comme solution de secours
                this.triggerCreatePaiement = true;
                this.cdr.detectChanges();

                // Réinitialiser après un court délai
                setTimeout(() => {
                    this.triggerCreatePaiement = false;
                    this.cdr.detectChanges();
                }, 100);
            }
        } else {
            console.warn('⚠️ [validatePaiement] L\'onglet actif n\'est pas "nouveau-paiement":', this.activeTab);
            this.toastr.warning('Veuillez sélectionner l\'onglet "Nouveau paiement"', 'Attention');
        }
    }

    onPaiementCreated(event: Paiement | { paiement: Paiement; avoirStatutId: number; avoirStatutLabel?: string }): void {
        const payload = (event && typeof event === 'object' && 'paiement' in event) ? (event as { paiement: Paiement; avoirStatutId: number; avoirStatutLabel?: string }) : null;
        const paiement = payload ? payload.paiement : (event as Paiement);
        const avoirStatutIdFromChild = payload?.avoirStatutId;
        const avoirStatutLabelFromChild = payload?.avoirStatutLabel;

        console.log('═══════════════════════════════════════════════════════════');
        console.log('✅ [onPaiementCreated] PAIEMENT CRÉÉ/MIS À JOUR AVEC SUCCÈS');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📦 [onPaiementCreated] Paiement:', paiement);
        console.log('📦 [onPaiementCreated] Paiement ID:', paiement.id);
        console.log('📦 [onPaiementCreated] Document ID:', paiement.document_id);
        console.log('📦 [onPaiementCreated] Paiements non affectés:', paiement.paiements_non_affectes?.length || 0);

        const statutUtiliseId = avoirStatutIdFromChild ?? 18;
        // Mise à jour en temps réel du statut de l'avoir (Utilisé) dans la ligne de la liste
        if (this.selectedDocumentType === 'avoir' && this.selectedFacture) {
            const fullData = this.selectedFacture.fullData || this.selectedFacture;
            fullData.statut_id = statutUtiliseId;
            if (fullData.statut && typeof fullData.statut === 'object') {
                fullData.statut.titre = 'Utilisé';
            }
            this.selectedFacture.statut_id = statutUtiliseId;
            this.selectedFacture.status = 'Utilisé';
            this.selectedFacture.statut = this.selectedFacture.statut && typeof this.selectedFacture.statut === 'object'
                ? { ...this.selectedFacture.statut, titre: 'Utilisé' }
                : 'Utilisé';
            this.cdr.detectChanges();
        }

        // Recalculer le reste à payer après création/mise à jour
        this.calculateResteAPayer();

        // Réinitialiser l'avoir sélectionné après paiement créé (l'avoir sera passé en "Utilisé" par nouveau-paiement)
        this.selectedAvoir = null;

        // Recharger la liste « Depuis un avoir » au prochain affichage de l’onglet (refreshTrigger + loadAvoirs si le composant est déjà affiché)
        this.refreshAvoirsTrigger = Date.now();
        const depuisAvoir = this.depuisAvoirComponents?.first;
        if (depuisAvoir) depuisAvoir.loadAvoirs();
        
        // Forcer la détection des changements pour mettre à jour le reste à payer dans le composant enfant
        this.cdr.detectChanges();

        // Recharger les paiements non affectés après création/mise à jour
        if (this.selectedFacture?.id) {
            console.log('🔄 [onPaiementCreated] Rechargement des paiements non affectés pour document_id:', this.selectedFacture.id);
            this.cdr.detectChanges();

            // Forcer le rechargement en déclenchant ngOnChanges via @Input()
            // Le composant se rechargera automatiquement si l'onglet est actif
            setTimeout(() => {
                const paiementNonAffComponent = this.paiementNonAffComponents?.first;
                if (paiementNonAffComponent && this.selectedFacture?.id) {
                    console.log('✅ [onPaiementCreated] Rechargement manuel des paiements non affectés');
                    paiementNonAffComponent.loadPaiementsNonAffectes(this.selectedFacture.id);
                } else {
                    console.log('ℹ️ [onPaiementCreated] Composant non disponible, rechargement automatique via @Input() lors du changement d\'onglet');
                }
            }, 300);
        }

        // Émettre l'événement pour notifier le composant parent (shared) de rafraîchir les données
        if (this.paiementSuccess) {
            const successPayload: { paiement: Paiement, documentId: number, avoirDocumentId?: number, avoirStatutId?: number, avoirStatutLabel?: string } = {
                paiement: paiement,
                documentId: paiement.document_id || this.selectedFacture?.id
            };
            if (this.selectedDocumentType === 'avoir' && this.selectedFacture?.id) {
                successPayload.avoirDocumentId = this.selectedFacture.id;
                successPayload.avoirStatutId = statutUtiliseId;
                successPayload.avoirStatutLabel = avoirStatutLabelFromChild ?? 'Utilisé';
            }
            this.paiementSuccess.emit(successPayload);
        }

        // Fermer le modal après création réussie
        if (this.modalRef) {
            setTimeout(() => {
                this.modalRef.close('Paiement créé');
            }, 1000); // Délai pour voir le message de succès et permettre le rechargement
        }
    }

    getPartyName(item: any): string {
        return item?.clientName || item?.fournisseurName || '--';
    }

    /**
     * Extraire le client_id depuis la facture sélectionnée
     */
    getClientId(facture: any): number | null {
        if (!facture) return null;
        
        // Essayer plusieurs chemins pour trouver le client_id
        const fullData = facture.fullData || facture;
        
        let clientId: number | null = null;
        
        // Chemin 1: fullData.client_data.client_id (structure la plus courante)
        if (fullData.client_data && fullData.client_data.client_id) {
            clientId = fullData.client_data.client_id;
            console.log('🔍 [getClientId] Trouvé via fullData.client_data.client_id:', clientId);
            return Number(clientId);
        }
        
        // Chemin 2: facture.client_data.client_id
        if (facture.client_data && facture.client_data.client_id) {
            clientId = facture.client_data.client_id;
            return Number(clientId);
        }
        
        // Chemin 3: fullData.clients[0] - vérifier pivot.client_id EN PREMIER (c'est souvent le vrai client_id)
        if (fullData.clients && Array.isArray(fullData.clients) && fullData.clients.length > 0) {

            // PRIORITÉ 1: Essayer de trouver le client_id dans le pivot (c'est souvent le vrai client_id)
            for (const client of fullData.clients) {
                if (client.pivot && client.pivot.client_id) {
                    clientId = client.pivot.client_id;
                    return Number(clientId);
                }
            }
            
            // PRIORITÉ 2: Essayer client.client_id directement
            for (const client of fullData.clients) {
                if (client.client_id) {
                    clientId = client.client_id;
                    return Number(clientId);
                }
            }
            
            // PRIORITÉ 3: Prendre le premier client.id (peut être un ID de relation pivot)
            clientId = fullData.clients[0].id || null;
            if (clientId) {
                return Number(clientId);
            }
        }
        
        // Chemin 4: fullData.client_id
        if (fullData.client_id) {
            clientId = fullData.client_id;
            return Number(clientId);
        }
        
        // Chemin 5: facture.client_id
        if (facture.client_id) {
            clientId = facture.client_id;
            return Number(clientId);
        }
        
        // Chemin 6: facture.clients[0].id
        if (facture.clients && Array.isArray(facture.clients) && facture.clients.length > 0) {
            clientId = facture.clients[0].id || null;
            if (clientId) {
                return Number(clientId);
            }
        }
        
        // Chemin 7: fullData.client?.id
        if (fullData.client && fullData.client.id) {
            clientId = fullData.client.id;
            return Number(clientId);
        }
        
        // Chemin 8: facture.client?.id
        if (facture.client && facture.client.id) {
            clientId = facture.client.id;
            return Number(clientId);
        }

        return null;
    }

    getFournisseurId(facture: any): number | null {
        if (!facture) return null;
        const fullData = facture.fullData || facture;
        if (fullData.fournisseur_id) return Number(fullData.fournisseur_id);
        if (fullData.fornisseur_data?.fournisseur_id) return Number(fullData.fornisseur_data.fournisseur_id);
        if (fullData.fornisseurs?.[0]?.pivot?.fournisseur_id) return Number(fullData.fornisseurs[0].pivot.fournisseur_id);
        if (fullData.fornisseurs?.[0]?.fournisseur_id) return Number(fullData.fornisseurs[0].fournisseur_id);
        if (fullData.fornisseurs?.[0]?.id) return Number(fullData.fornisseurs[0].id);
        if (fullData.fournisseur?.id) return Number(fullData.fournisseur.id);
        if (facture.fournisseur_id) return Number(facture.fournisseur_id);
        if (facture.fornisseurs?.[0]?.fournisseur_id) return Number(facture.fornisseurs[0].fournisseur_id);
        if (facture.fornisseurs?.[0]?.id) return Number(facture.fornisseurs[0].id);
        return null;
    }

    getDocumentOriginalId(facture: any): number | null {
        if (!facture) return null;
        const fullData = facture.fullData || facture;
        const id = fullData.document_original_id ?? facture.document_original_id ?? fullData.document_original ?? facture.document_original;
        if (id != null) return Number(id);
        return null;
    }

    private cachedStationId: number | null = null;

    getStationId(): number | null {
        // Utiliser le cache si disponible
        if (this.cachedStationId !== null) {
            return this.cachedStationId;
        }

        try {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            // Essayer plusieurs chemins pour trouver le station_id
            const stationId = currentUser?.stations?.[0]?.id || 
                             currentUser?.station_id || 
                             currentUser?.stations?.[0]?.station_id ||
                             null;
            
            if (stationId) {
                this.cachedStationId = Number(stationId);
            } else {
                console.warn('⚠️ [getStationId] Aucun station_id trouvé dans currentUser');
            }
            
            return this.cachedStationId;
        } catch (error) {
            return null;
        }
    }

    calculateResteAPayer(): void {
        if (!this.selectedFacture?.id) {
            this.resteAPayer = this.factureMontant;
            return;
        }

        // Charger les paiements pour calculer le reste
        this.paiementService.getPaiementsByDocumentId(this.selectedFacture.id).subscribe({
            next: (response: any) => {
                // Extraire les paiements de la réponse encapsulée
                let paiements: Paiement[] = [];
                if (Array.isArray(response)) {
                    paiements = response;
                } else if (response && response.data) {
                    if (Array.isArray(response.data)) {
                        paiements = response.data;
                    } else if (response.data.paiements && Array.isArray(response.data.paiements)) {
                        paiements = response.data.paiements;
                    } else if (response.data.paiement && typeof response.data.paiement === 'object') {
                        paiements = [response.data.paiement];
                    }
                } else if (response && response.id) {
                    paiements = [response];
                }

                let totalPaye = 0;
                for (const paiement of paiements) {
                    // Calculer le total des montants des lignes de paiement
                    for (const ligne of paiement.lignes_paiement || []) {
                        totalPaye += parseFloat(ligne.montant || '0');
                    }
                }
                // Calcul simple : reste à payer = factureMontant - totalPaye (sans tenir compte de la retenue à la source)
                this.resteAPayer = Math.max(0, this.factureMontant - totalPaye);
                
                console.log('💰 [calculateResteAPayer] Calcul du reste à payer:');
                console.log('   - factureMontant:', this.factureMontant);
                console.log('   - totalPaye:', totalPaye);
                console.log('   - resteAPayer:', this.resteAPayer);
                
                // Forcer la détection des changements pour mettre à jour le composant enfant
                this.cdr.detectChanges();
            },
            error: () => {
                this.resteAPayer = this.factureMontant;
            }
        });
    }

    /**
     * Détermine si le document est un document d'achat (facture fournisseur, commande fournisseur, bon réception)
     */
    private isAchatDocument(typeDocument: string): boolean {
        const typeUpper = typeDocument.toUpperCase();
        return ['FACTURE FOURNISSEUR', 'COMMANDE FOURNISSEUR', 'BON RECEPTION', 'BON DE RECEPTION'].includes(typeUpper);
    }
    getMontantFacture(facture: any): string {
        if (!facture) return '—';
        const fullData = facture.fullData || facture;
        const netPayer = fullData.net_payer ?? facture.net_payer;
        if (netPayer == null) return facture.revenue ?? '—';
        const num = parseFloat(String(netPayer).replace(',', '.'));
        return isNaN(num) ? String(netPayer) : num.toFixed(2).replace('.', ',') + ' TND';
    }

    /** Retourne le libellé du statut du document (ex. "Non utilisé", "Partiellement utilisé") pour le nouveau-paiement. */
    getDocumentStatut(facture: any): string | null {
        if (!facture) return null;
        const fullData = facture.fullData || facture;
        const s = fullData.statut ?? fullData.status ?? facture.statut ?? facture.status;
        if (s == null) return null;
        // Statut peut être un objet { id, titre } (API) ou une chaîne ; facture.status = libellé dans la liste (DataService)
        const label = typeof s === 'object' ? (s.titre ?? s.libelle ?? s.label ?? '') : String(s);
        const trimmed = (label || '').trim();
        return trimmed || null;
    }

    /** IDs statut avoir : 19 = Non utilisé, 20 = Partiellement utilisé (aligné avec depuis-avoir.component). */
    private readonly STATUT_AVOIR_IDS = [19, 20];

    /** True si statut_id vaut 19 (Non utilisé) ou 20 (Partiellement utilisé). */
    private isStatutAvoirById(statutId: any): boolean {
        const id = statutId != null ? Number(statutId) : NaN;
        return !isNaN(id) && this.STATUT_AVOIR_IDS.includes(id);
    }

    /** True si le libellé du statut correspond à un avoir (Non utilisé ou Partiellement utilisé), insensible casse/accents. */
    private isStatutAvoirByLabel(statutLabel: string | null): boolean {
        if (!statutLabel) return false;
        const s = statutLabel.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // minuscules, sans accents
        return s === 'non utilise' || s === 'partiellement utilise';
    }

    /** Retourne le statut_id du document pour le nouveau-paiement (19 = Non utilisé, 20 = Partiellement utilisé). */
    getDocumentStatutId(facture: any): number | null {
        if (!facture) return null;
        const fullData = facture.fullData || facture;
        const id = fullData.statut_id ?? fullData.statut?.id ?? facture.statut_id ?? facture.statut?.id;
        return id != null ? Number(id) : null;
    }

}
